import { useRef } from 'react'

import { useTabOwnerId } from '@/components/layout/SheetContainerContext'
import { useMountEffect } from '@/hooks/useMountEffect'
import {
  publishTabDirty,
  useEditorRegistry,
  type EditorEntry,
} from '@/stores/editorRegistry'

/**
 * Registers the calling card as one of its tab's editors, so the tab's single
 * close owner can flush it and the shelf can show the tab as dirty.
 *
 * Outside a tab tree (`useTabOwnerId()` is null) this is a no-op.
 *
 * Registration is mount-scoped, exactly like `useWorkspaceCloseOwner`: the tab
 * id is fixed for the lifetime of the component and the registered object is a
 * stable proxy forwarding to the latest entry, so an inline entry object is
 * free and the unregister is a plain unmount cleanup.
 */
export function useRegisterTabEditor(entry: EditorEntry): void {
  const tabId = useTabOwnerId()

  const entryRef = useRef(entry)
  entryRef.current = entry

  const proxy = useRef<EditorEntry>({
    get label() {
      return entryRef.current.label
    },
    isDirty: () => entryRef.current.isDirty(),
    save: () => entryRef.current.save(),
  }).current

  // Dirty is derived from the card's own state, so the card re-renders the
  // moment it flips. Publish that flip from render rather than an effect, and
  // only on the edge: the microtask keeps the store write out of the render
  // pass, and it re-reads every entry through its proxy. Seeded with the
  // current value, so a mount costs no microtask.
  const dirty = entry.isDirty()
  const publishedRef = useRef(dirty)
  if (tabId && publishedRef.current !== dirty) {
    publishedRef.current = dirty
    queueMicrotask(() => publishTabDirty(tabId))
  }

  useMountEffect(() => {
    if (!tabId) return
    const store = useEditorRegistry.getState()
    store.register(tabId, proxy)
    return () => store.unregister(tabId, proxy)
  })
}
